// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MITs
import { Injectable, Inject } from '@angular/core';
import { TypedStorage } from '@models';
import { Observable } from 'rxjs';
import { localStorageToken } from '@lfx/config';

export const bucketName = 'lfxBucket';

@Injectable({ providedIn: 'root' })
export class StorageService implements TypedStorage {
  private bucket: { [key: string]: any } = {};

  constructor(@Inject(localStorageToken) private storageEngine: Storage) {
    try {
      const stringifiedBucket = this.storageEngine?.getItem(bucketName);
      this.bucket = stringifiedBucket ? JSON.parse(stringifiedBucket) : {};
    } catch (error) {
      console.warn('Failed to initialize storage bucket:', error);
      this.bucket = {};
    }
  }

  getItem<T>(key: string): Observable<T> {
    return new Observable(observer => {
      if (!this.bucket.hasOwnProperty(key)) {
        console.warn(`Cannot get unsaved property from storage => ${key}`);
      }

      const item: T = this.bucket[key];
      observer.next(item);
      return observer.complete();
    });
  }

  setItem<T>(key: string, value: T): void {
    try {
      this.bucket[key] = value;
      this.storageEngine?.setItem(bucketName, JSON.stringify(this.bucket));
    } catch (error) {
      console.warn('Failed to set item in storage:', error);
    }
  }

  getItemSync(key: string) {
    return this.bucket[key];
  }

  removeItem(key: string) {
    try {
      this.bucket[key] = null;
      this.storageEngine?.setItem(bucketName, JSON.stringify(this.bucket));
    } catch (error) {
      console.warn('Failed to remove item from storage:', error);
    }
  }

  clear(key: string) {
    try {
      delete this.bucket[key];
      this.storageEngine?.setItem(bucketName, JSON.stringify(this.bucket));
    } catch (error) {
      console.warn('Failed to clear item from storage:', error);
    }
  }
}
